import React, {
  useEffect,
  forwardRef,
  useImperativeHandle,
  useRef,
  useContext,
  useState,
} from "react";
import {
  AdyenCheckout,
  Dropin,
  Card,
  ApplePay,
  GooglePay,
} from "@adyen/adyen-web";
import "@adyen/adyen-web/styles/adyen.css";
import "../../adyen-overrides.css";
import { useNavigate } from "react-router-dom";
import { initCardPayment, finalizeCardPayment } from "@api";
import {
  isApplePayCancellation,
  getTranslations,
  navigateOnPaymentCompletion,
  buildDropinConfiguration,
  onSubmitFactory,
  resolveInitData,
  scrollFocusedFieldIntoView,
  revealPaymentError,
} from "../shared";

const AdyenCorpPersAdvancedFlow = ({
  isCorporate,
  paymentParameters, // { amount, currency, reference, returnUrl, … }
  onSuccess,
  processingUrl,
  onError,
  externalSubmitRef,
  onAmountChange, // New callback for amount changes
  setLoading, // Simple loading state setter
  onCardTypeChange, // New callback for card type detection
  onAmexChange, // New callback for Amex detection
  onCardBrandChange, // Callback for notifying if a recognised card brand has been entered
  onCardNotAllowed,
  onStorePaymentDetails,
  setProcessing,
  disablePayment,
  setBinLookupPending,
}) => {
  const navigate = useNavigate();
  const corporateDropinRef = useRef(null);
  const personalDropinRef = useRef(null);
  const [errorMessage, setErrorMessage] = useState(null);
  const [corporateCheckout, setCorporateCheckout] = useState(null);
  const [personalCheckout, setPersonalCheckout] = useState(null);
  const [corporatePaymentData, setCorporatePaymentData] = useState(null);
  const [personalPaymentData, setPersonalPaymentData] = useState(null);
  const [isInitialized, setIsInitialized] = useState(false);
  const paymentDataRef = useRef(null);
  const isCorporateRef = useRef(null);
  const disablePaymentRef = useRef(disablePayment);

  useEffect(() => {
    disablePaymentRef.current = disablePayment;
  }, [disablePayment]);

  // Secured card fields report focus via the card onFocus config; the billing-address
  // inputs are plain inputs, so catch their focus here to scroll them up too (mobile).
  useEffect(() => {
    const container = document.getElementById("adyen-dropin-container");
    if (!container) return;
    const onFocusIn = () => scrollFocusedFieldIntoView();
    container.addEventListener("focusin", onFocusIn);
    return () => container.removeEventListener("focusin", onFocusIn);
  }, []);

  useEffect(() => {
    isCorporateRef.current = isCorporate;
  }, [isCorporate]);

  // Helper function to create checkout configuration
  const createCheckoutConfig = (paymentData, cardAccountTypeParam) => {
    const { paymentMethods, clientKey, environment } = JSON.parse(
      paymentData.clientData,
    );

    const adyenPaymentMethodsData = JSON.parse(paymentMethods);

    return {
      countryCode: "GB",
      paymentMethodsResponse: adyenPaymentMethodsData,
      showPayButton: false,
      environment: environment.toLowerCase(),
      clientKey,
      onSubmit: onSubmitFactory({
        disablePaymentRef,
        paymentParameters,
        onCardNotAllowed,
        onError,
        setProcessing,
        authoritativeAmount: paymentData?.amount,
        onStorePaymentDetails,
        paymentDataRefRef: paymentDataRef,
        cardAccountType: cardAccountTypeParam,
      }),

      onAdditionalDetails: async (state, component, actions) => {
        try {
          const finalizeCardPaymentResponse = await finalizeCardPayment({
            merchantId: paymentParameters.merchantId,
            paymentId: paymentDataRef.current?.id,
            clientData: JSON.stringify(state.data),
            paymentProvider: "Adyen",
          });

          const result = JSON.parse(finalizeCardPaymentResponse.clientData);

          // If the /payments/details request from your server fails, or if an unexpected error occurs.
          if (!result.resultCode) {
            actions.reject();
            return;
          }

          const { resultCode, action, order, donationToken } = result;

          actions.resolve({
            resultCode,
            action,
            order,
            donationToken,
          });
        } catch (error) {
          console.error("onAdditionalDetails", error);
          actions.reject();
        }
      },
      onPaymentCompleted: (result) => {
        console.log("Payment completed:", result);

        // https://docs.adyen.com/online-payments/build-your-integration/payment-result-codes/
        if (
          result.resultCode === "Authorised" ||
          result.resultCode === "Pending" ||
          result.resultCode === "Received"
        ) {
          navigateOnPaymentCompletion({
            navigate,
            onSuccess,
            baseUrl: processingUrl,
            paymentId: paymentDataRef.current?.id,
          });
        } else {
          // This path should never be reached, because declined payments trigger onPaymentFailed rather than onPaymentCompleted
          setErrorMessage("Your payment was declined. Please try again.");
          onError();
          revealPaymentError();
          setProcessing?.(false);
        }
      },
      onChange: (state, dropin) => {
        if (!state.isValid) {
          onError();
        }
      },
      onError: (error) => {
        if (isApplePayCancellation(error)) {
          console.log("Apple Pay cancelled by user - not showing error");
          setErrorMessage(null);
          initBothCheckouts();
        } else if (error?.cause?.statusCode === "CANCELED") {
          // Keep the original check for other cancellation types
          setErrorMessage(null);
          initBothCheckouts();
        } else {
          console.error(error);
          setErrorMessage("An unknown error occurred. Please try again later.");
          revealPaymentError();
          onError?.(error);
        }
      },
      onPaymentFailed: (result) => {
        console.warn("Payment failed", result);
        setErrorMessage(
          "Payment failed. Please check your details and try again or use a different payment method.",
        );
        onError?.();
        const activeDropin = isCorporate
          ? corporateDropinRef.current
          : personalDropinRef.current;
        activeDropin?.setStatus("ready");
        revealPaymentError();
      },
      locale: "en-US",
      translations: getTranslations(),
    };
  };

  // Helper function to create dropin configuration
  const createDropinConfig = (paymentData) => {
    const config = buildDropinConfiguration({
      paymentMethodComponents: [Card],
      paymentParameters,
      setLoading,
      onAmexChange,
      onCardBrandChange,
      onCardTypeChange,
      setBinLookupPending,
      instantPaymentTypes: [],
    });
    // Only enable AMEX for corporate when allowed (align with sessions corp/pers)
    if (paymentParameters?.amexPaymentAllowed) {
      const brands = config?.paymentMethodsConfiguration?.card?.brands;
      if (Array.isArray(brands)) {
        if (isCorporate) {
          if (!brands.includes("amex")) {
            brands.push("amex");
          }
        } else {
          const idx = brands.indexOf("amex");
          if (idx > -1) {
            brands.splice(idx, 1);
          }
        }
      }
    }
    return config;
  };

  // Initialize both payment data and checkouts once
  const initBothCheckouts = async () => {
    try {
      // Create both payment requests
      const [corpInitResponse, persInitResponse] = await Promise.all([
        initCardPayment({
          merchantId: paymentParameters.merchantId,
          amount: paymentParameters.amount,
          cardAccountType: "Corporate",
          paymentProvider: "Adyen",
        }),
        initCardPayment({
          merchantId: paymentParameters.merchantId,
          amount: paymentParameters.amount,
          cardAccountType: "Personal",
          paymentProvider: "Adyen",
        }),
      ]);

      const corpPaymentData = resolveInitData(corpInitResponse, "corporate");
      const persPaymentData = resolveInitData(persInitResponse, "personal");

      setCorporatePaymentData(corpPaymentData);
      setPersonalPaymentData(persPaymentData);

      // Create checkouts for both
      const corporateConfig = createCheckoutConfig(
        corpPaymentData,
        "Corporate",
      );
      const personalConfig = createCheckoutConfig(persPaymentData, "Personal");

      const [corpCheckout, persCheckout] = await Promise.all([
        AdyenCheckout(corporateConfig),
        AdyenCheckout(personalConfig),
      ]);

      setCorporateCheckout(corpCheckout);
      setPersonalCheckout(persCheckout);
      setIsInitialized(true);

      setLoading?.(false); // Notify parent that initialization completed
    } catch (error) {
      console.error("Failed to initialise checkouts:", error);
      setErrorMessage("Failed to initialise payment. Please try again.");
      revealPaymentError();
      onError?.(error);
      setLoading?.(false); // Notify parent that initialization completed (with error)
    }
  };

  useEffect(() => {
    let mounted = true;

    setLoading?.(true); // Notify parent that initialization started

    const init = async () => {
      if (!mounted) return;
      await initBothCheckouts();
    };

    init();

    return () => {
      mounted = false;
      // Clean up both dropins
      corporateDropinRef.current?.unmount();
      personalDropinRef.current?.unmount();
    };
  }, []);

  // Mount/unmount dropins based on isCorporate
  useEffect(() => {
    if (
      !isInitialized ||
      !corporateCheckout ||
      !personalCheckout ||
      !corporatePaymentData ||
      !personalPaymentData
    ) {
      return;
    }

    // Unmount both first
    corporateDropinRef.current?.unmount();
    personalDropinRef.current?.unmount();
    corporateDropinRef.current = null;
    personalDropinRef.current = null;

    // Show skeleton while new drop-in mounts
    setLoading?.(true);

    // Mount the appropriate one
    if (isCorporate) {
      const dropin = new Dropin(
        corporateCheckout,
        createDropinConfig(corporatePaymentData),
      ).mount("#adyen-dropin-container");
      corporateDropinRef.current = dropin;
    } else {
      const dropin = new Dropin(
        personalCheckout,
        createDropinConfig(personalPaymentData),
      ).mount("#adyen-dropin-container");
      personalDropinRef.current = dropin;
    }

    // Notify parent of current amount
    const currentPaymentData = isCorporate
      ? corporatePaymentData
      : personalPaymentData;
    onAmountChange?.(currentPaymentData.amount);

    return () => {
      corporateDropinRef.current?.unmount();
      personalDropinRef.current?.unmount();
    };
  }, [
    isCorporate,
    isInitialized,
    corporateCheckout,
    personalCheckout,
    corporatePaymentData,
    personalPaymentData,
  ]);

  useEffect(() => {
    setErrorMessage(null);
  }, [isCorporate]);

  // Notify parent of amount when payment data is first initialized
  useEffect(() => {
    if (isInitialized && corporatePaymentData && personalPaymentData) {
      const currentPaymentData = isCorporate
        ? corporatePaymentData
        : personalPaymentData;
      console.log("currentPaymentData", currentPaymentData);
      onAmountChange?.(currentPaymentData.amount);
    }
  }, [isInitialized, corporatePaymentData, personalPaymentData, isCorporate]);

  // expose programmatic submit if parent wants it
  useImperativeHandle(externalSubmitRef, () => ({
    submitPayment: () => {
      const activeDropin = isCorporate
        ? corporateDropinRef.current
        : personalDropinRef.current;
      if (activeDropin) {
        console.log("Submitting Adyen payment via Drop-in");
        setErrorMessage(null);
        activeDropin.submit();
      } else {
        console.warn("Drop-in is not mounted yet");
      }
    },
  }));

  return (
    <>
      {errorMessage && (
        <div className="text-red-600 text-sm rounded-3 p-2 mb-4" role="alert">
          {errorMessage}
        </div>
      )}
      <div id="adyen-dropin-container" />
    </>
  );
};

export { AdyenCorpPersAdvancedFlow };
